import { FormProvider, useForm } from 'react-hook-form'

import { QueryHookOptions, useQuery } from '@apollo/client'
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import {
  AvailablePayrollConceptFieldFragment,
  EditCompensationsFormDocument,
  EditCompensationsFormQuery,
  EditCompensationsFormQueryVariables,
  EmployeePayrollRunElementEditCompensationsFormFragment,
  PayrollRunEmployeesCompensationFormFragment,
} from 'generated/resources/graphql'

import { CompensationsFormFields } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormFields'
import { mapCompensationAmountsFromCents } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormFields/CompensationAmountInputField/CompensationAmountInputFieldUtils'
import { useIntegrationsSyncContext } from 'modules/integrations/public'

function useEditCompensationsFormQuery(
  baseOptions: QueryHookOptions<EditCompensationsFormQuery, EditCompensationsFormQueryVariables>
) {
  const options = { ...baseOptions }
  return useQuery<EditCompensationsFormQuery, EditCompensationsFormQueryVariables>(
    EditCompensationsFormDocument,
    options
  )
}

type EmployeePayrollRunElementEditCompensationsForm =
  EmployeePayrollRunElementEditCompensationsFormFragment['nodes']

type EditCompensationsFormProps = {
  employeeId: number
  payrollRunId: number
  employeePayrollRunInputRowEmployeePayrollRun: EmployeePayrollRunElementEditCompensationsFormFragment
  availablePayrollConcepts: Array<AvailablePayrollConceptFieldFragment>
  readonly?: boolean
  payrollRunEmployeeRowCompensations?: PayrollRunEmployeesCompensationFormFragment[]
}

function EditCompensationsForm({
  employeeId,
  payrollRunId,
  employeePayrollRunInputRowEmployeePayrollRun,
  availablePayrollConcepts,
  readonly,
  payrollRunEmployeeRowCompensations,
}: EditCompensationsFormProps) {
  const defaultValues = mapCompensationAmountsFromCents(
    employeePayrollRunInputRowEmployeePayrollRun
  )
  const methods = useForm<EmployeePayrollRunElementEditCompensationsFormFragment>({
    defaultValues,
    resetOptions: {
      keepDirtyValues: false,
    },
    mode: 'onChange',
  })

  return (
    <FormProvider {...methods}>
      <form>
        <CompensationsFormFields
          employeeId={employeeId}
          payrollRunId={payrollRunId}
          availablePayrollConcepts={availablePayrollConcepts}
          payrollRunEmployeeCompensations={payrollRunEmployeeRowCompensations}
          readonly={readonly}
        />
      </form>
    </FormProvider>
  )
}

function EditCompensationsFormStandalone({
  employeeId,
  payrollRunId,
  ...props
}: { employeeId: number; payrollRunId: number } & Omit<
  EditCompensationsFormProps,
  'employeePayrollRunInputRowEmployeePayrollRun' | 'payrollRunEmployeeRowCompensations'
>) {
  const { marketplaceIntegration } = useIntegrationsSyncContext()

  const { data, loading, error } = useEditCompensationsFormQuery({
    variables: {
      employeeId,
      payrollRunId,
      marketplaceIntegrationId: marketplaceIntegration?.id,
    },
  })

  if (error) {
    throw new Error('Error fetching data')
  }

  if (loading) {
    return (
      <Box padding="s40" width="full" alignItems="center">
        <Spinner />
      </Box>
    )
  }

  const employeePayrollRunInputRowEmployeeRun =
    data?.compensations?.employeesCompensationsConnection
  const payrollRunEmployeeRowCompensations =
    data?.compensations?.payrollRunEmployeesCompensationsConnection?.nodes ?? []

  return (
    employeePayrollRunInputRowEmployeeRun && (
      <EditCompensationsForm
        employeeId={employeeId}
        payrollRunId={payrollRunId}
        employeePayrollRunInputRowEmployeePayrollRun={employeePayrollRunInputRowEmployeeRun}
        payrollRunEmployeeRowCompensations={payrollRunEmployeeRowCompensations}
        {...props}
      />
    )
  )
}

export { EditCompensationsForm, EditCompensationsFormStandalone }
export type { EmployeePayrollRunElementEditCompensationsForm }
